import { useState } from 'react';
import { Document } from '@roc/feature-types';
import { useDocumentStore } from 'libs/feature-documents/src/documents/hooks/useDocumentStore';
import { useEffect } from 'react';
import { FastTrackModal } from '@roc/feature-loans'
import { LoanStatus } from '@roc/feature-utils';
import { observer } from 'mobx-react';
import { useBaseStore } from '@roc/feature-app-core';
import AddtionalFastTrackData from './AdditionalFastTrackData';
import { DocumentName } from 'libs/feature-documents/src/documents/constants/documents';

export type LoanFastTrackPaymentDialogProps = {
  open?: boolean;
  onClose?: () => void;
  loan: any;
  document: Document;
};

const fastTrackElegibleLoanStatuses = [
  LoanStatus.UNKNOWN,
  LoanStatus.PENDING,
  LoanStatus.TERM_SHEET_OUT,
  LoanStatus.IN_UNDERWRITING,
  LoanStatus.PRESCREEN,
  LoanStatus.PENDING_DECISION,
  LoanStatus.PENDING_TERM_SHEET,
  LoanStatus.AGREED,
  LoanStatus.PENDING_SUBMISSION
];

const LoanFastTrackPaymentDialog = observer((props: LoanFastTrackPaymentDialogProps) => {
  const { open, onClose, loan, document } = props;

  const { documentStore } = useDocumentStore();
  const { globalStore } = useBaseStore();
  const { documentFormStore, closingDocuments, underwritingDocuments } = documentStore;
  const { loanFastrackPaymentStore, borrowerClosingAgentStore } = documentFormStore;
  const { canEdit: canEditBorrowerInfo } = borrowerClosingAgentStore;
  const [closingAgentDoc, setClosingAgentDoc] = useState<Document | null>(null);
  const [titleInfoDoc, setTitleInfoDoc] = useState<Document | null>(null);
  const [fastTrackDataLoaded, setFastTrackDataLoaded] = useState(false);
  const fastTrackEnabled = !!globalStore.userFeatures?.fastTrack;

  // Load on open (not on mount): this dialog is mounted with DocumentForms,
  // often before the loan for the clicked task is loaded (e.g. from the tasks grid)
  useEffect(() => {
    setFastTrackDataLoaded(false);
    if (!open || !fastTrackEnabled || !loan?.loanId) {
      return;
    }
    loanFastrackPaymentStore.reset();
    Promise.all([
      loanFastrackPaymentStore.loadFastTrackFees(),
      loan?.fastTrack
        ? loanFastrackPaymentStore.loadFastTrackInfo(loan?.loanId)
        : loanFastrackPaymentStore.fetchDraftLoanId(),
    ]).finally(() => setFastTrackDataLoaded(true));
    setRequiredDocs();
  }, [open, fastTrackEnabled, loan?.loanId, loan?.fastTrack]);

  const isOpen = open && fastTrackEnabled && fastTrackDataLoaded;

  const setRequiredDocs = () => {
    const todosByLoanProcess = documentStore.loanTodosStore?.todosByLoanProcess || {};
    const docs = Object.values(todosByLoanProcess).reduce((acc, arr) => acc.concat(arr), []);
    docs.forEach(doc => {
      if(doc?.title === DocumentName.BORROWER_CLOSING_AGENT){
        setClosingAgentDoc(doc?.document as Document);
      }
      if(doc?.title === DocumentName.TITLE_AGENT){
        setTitleInfoDoc(doc?.document as Document);
      }
    })
  }
  const onSubmitBorrowerInfo = () => {
    if (closingAgentDoc) {
      borrowerClosingAgentStore.submitBorrowerAgent(closingAgentDoc);
    }
    else{
      globalStore.notificationStore.showErrorNotification({
        message: 'Borrower Closing Agent document not found. Please upload the document and try again.'
      })
    }
  }

  const onSubmitTitleInfo = () => {
    if (titleInfoDoc) {
      loanFastrackPaymentStore.submitTitleInfo(titleInfoDoc);
    }
    else{
      globalStore.notificationStore.showErrorNotification({
        message: 'Title Provider Information document not found. Please upload the document and try again.'
      })
    }
  }

  const onSubmit = () => {
    onSubmitBorrowerInfo();
    onSubmitTitleInfo();
  }

  const borrowerList = (borrowers) => {
    return borrowers.map(borrower => {
      return {
        ...borrower,
        roleInTransaction: ''
      };
    });
  }
  return (loanFastrackPaymentStore.loanFastTrackInfo?.paymentStatus) ?
    (
      <AddtionalFastTrackData
        open={isOpen}
        onClose={onClose}
        fastTrackFees={loanFastrackPaymentStore.fastTrackFees}
        fastTrackInfo={loanFastrackPaymentStore.loanFastTrackInfo}
        canEditBorrowerInfo={canEditBorrowerInfo}
        onSubmit={onSubmit}
        properties={loan?.properties}
        underwritingDocuments={underwritingDocuments || {}}
        closingDocuments={closingDocuments || {}}
        onDocumentUpload={(file: any, docRef: any, loanProcess: any, onSuccess?: () => any, drawId?: any) => documentStore.uploadDocument(file, docRef, loanProcess, onSuccess, drawId)}
        loanFastrackPaymentStore={loanFastrackPaymentStore}
        loan={loan}
        taskId={document?.taskId}
      />
    ) :
    (
      fastTrackElegibleLoanStatuses.includes(loan?.status) && (<FastTrackModal
        isOpen={isOpen}
        onClose={onClose}
        onUpload={(fileUploads) => loanFastrackPaymentStore.uploadInsuranceDocument(fileUploads[0])}
        onConfirm={options => {
          loanFastrackPaymentStore.updateLoanFastrackInfo(options, loan?.loanId);
        }}
        sendPaymentEmail={() => loanFastrackPaymentStore.requestFastTrackPaymentEmail({
          loanId: loanFastrackPaymentStore.loanFastTrackInfo?.randomLoanId || loanFastrackPaymentStore.draftLoanId,
          borrowers: borrowerList(loan?.borrowers),
        })}
        options={loanFastrackPaymentStore.loanFastTrackInfo || { insuranceReview: false, titleSearch: false, insurance: false, rushedAppraisal: false, titleAttorneySearch: false }}
        prefInsurance={loan?.loanInsurance?.insuredThroughElmsure ? 'Elmsure' : ''}
        prefTitle={loan?.titleCompany?.name?.includes('Wimba') ? 'Wimba' : ''}
        fastTrackFees={loanFastrackPaymentStore.fastTrackFees}
        propertiesQuantity={loan?.properties?.length}
        paymentUrl={loanFastrackPaymentStore.fastTrackPaymentUrl}
        readOnly={loanFastrackPaymentStore.loanFastTrackInfo?.paymentStatus || !fastTrackElegibleLoanStatuses.includes(loan?.status)}
        canSubmitAppraisalWithTamarisk={loanFastrackPaymentStore.canSubmitAppraisalWithTamarisk}
        taskId={document?.taskId}
      />
      ));
});

export default LoanFastTrackPaymentDialog;
